import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  Avatar,
  Container,
  Fade,
  Grow,
  Stack,
  Chip,
} from "@mui/material";
import {
  CameraAlt as CameraIcon,
  BarChart as BarChartIcon,
  People as PeopleIcon,
  NotificationsActive as AlertsIcon,
  PhotoCamera as PhotoCameraIcon,
  LocationOn as LocationIcon,
  Timeline as TimelineIcon,
  ArrowForward as ArrowForwardIcon,
  Pets as PetsIcon,
  Forest as ForestIcon,
  Terrain as TerrainIcon,
  Nightlife as NightlifeIcon,
  Explore as ExploreIcon,
  Park as ParkIcon,
} from "@mui/icons-material";
import Hero from "../components/Hero";

// Import images for sections
import snowleopardImage from "../images/snowleopard.png";
import leopardImage from "../images/leopard.png";
import foxImage from "../images/fox.png";
import deerImage from "../images/deer.png";
import markhorImage from "../images/markhor.png";
import vehiclepersonImage from "../images/vehicleperson.png";
// import speciescollageImage from "../images/speciescollage.png";

export default function Home() {
  const [stats, setStats] = useState({
    cameras: 0,
    events: 0,
    species: 0,
    users: 0,
  });
  const [loading, setLoading] = useState(true);

  const gradientStyle = {
    background:
      "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #454545 100%)",
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStats({
          cameras: 24,
          events: 1247,
          species: 12,
          users: 156,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Species detection data
  const speciesDetected = [
    { name: "Leopard", count: 342, icon: <PetsIcon />, color: "#FF6B35" },
    { name: "Fox", count: 156, icon: <ForestIcon />, color: "#FF8C42" },
    { name: "Markhor", count: 89, icon: <TerrainIcon />, color: "#8B4513" },
    { name: "Deer", count: 278, icon: <ParkIcon />, color: "#4CAF50" },
    {
      name: "Snow Leopard",
      count: 124,
      icon: <NightlifeIcon />,
      color: "#795548",
    },
    { name: "Wolf", count: 67, icon: <PetsIcon />, color: "#607D8B" },
  ];

  const features = [
    {
      icon: <CameraIcon sx={{ fontSize: 40 }} />,
      title: "Real-time Monitoring",
      description: "Live camera feeds with instant wildlife detection alerts",
      color: "#4CAF50",
    },
    {
      icon: <BarChartIcon sx={{ fontSize: 40 }} />,
      title: "Advanced Analytics",
      description: "Comprehensive statistics for conservation insights",
      color: "#2196F3",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      title: "User Management",
      description: "Hierarchical system with role-based access control",
      color: "#FF9800",
    },
    {
      icon: <AlertsIcon sx={{ fontSize: 40 }} />,
      title: "Instant Alerts",
      description: "Real-time notifications for critical wildlife events",
      color: "#E91E63",
    },
  ];

  // Alternate sections data with actual images
  const sections = [
    {
      title: "Snow Leopard Detection",
      description:
        "Our AI-powered system automatically detects and classifies snow leopards in real-time. With over 95% accuracy, we help conservationists track and protect this endangered species.",
      image: snowleopardImage,
      imageLeft: true,
      stats: "95% Detection Accuracy",
    },
    {
      title: "Leopard Monitoring",
      description:
        "Monitor leopard populations with our advanced camera trap system. Get instant alerts when leopards are detected and track their movement patterns across different territories.",
      image: leopardImage,
      imageLeft: false,
      stats: "24/7 Live Monitoring",
    },
    {
      title: "Fox Population Tracking",
      description:
        "Track fox populations and their habitat usage with real-time data. Our system helps researchers understand fox behavior and movement patterns.",
      image: foxImage,
      imageLeft: true,
      stats: "156 Foxes Tracked",
    },
    {
      title: "Deer Conservation",
      description:
        "Monitor deer populations and migration patterns across protected areas. Our advanced analytics help conservationists make informed decisions.",
      image: deerImage,
      imageLeft: false,
      stats: "278 Deer Detected",
    },
    {
      title: "Markhor Protection",
      description:
        "Protect the majestic Markhor with our advanced monitoring system. Track population trends and habitat usage to support conservation efforts.",
      image: markhorImage,
      imageLeft: true,
      stats: "89 Markhor Tracked",
    },
    {
      title: "Vehicle & Person Detection",
      description:
        "Monitor human-wildlife interactions and potential threats. Our system detects vehicles and persons to help prevent poaching and human-wildlife conflicts.",
      image: vehiclepersonImage,
      imageLeft: false,
      stats: "Real-time Detection",
    },
  ];

  const statsCards = [
    {
      label: "Active Cameras",
      value: stats.cameras,
      icon: <PhotoCameraIcon />,
      color: "#4CAF50",
    },
    {
      label: "Total Events",
      value: stats.events,
      icon: <TimelineIcon />,
      color: "#2196F3",
    },
    {
      label: "Species Detected",
      value: stats.species,
      icon: <LocationIcon />,
      color: "#FF9800",
    },
    {
      label: "Active Users",
      value: stats.users,
      icon: <PeopleIcon />,
      color: "#9C27B0",
    },
  ];

  return (
    <Box sx={{ overflowX: "hidden" }}>
      {/* Hero Section with Slideshow */}
      <Hero />

      {/* Stats Section */}
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Grow in timeout={600}>
          <Box>
            <Typography
              variant="h3"
              fontWeight={700}
              gutterBottom
              sx={{
                textAlign: "center",
                mb: 2,
                color: "#8e8e8e",
                fontSize: { xs: "2rem", md: "3rem" },
                textShadow: "0 2px 20px rgba(0,0,0,0.3)",
              }}
            >
              System Overview
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                mb: 5,
                color: "rgba(104, 101, 101, 0.7)",
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Real-time tracking and monitoring of wildlife across protected
              areas
            </Typography>

            <Grid container spacing={3}>
              {statsCards.map((stat, index) => (
                <Grid item xs={6} sm={6} md={3} key={index}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      borderRadius: 4,
                      transition: "all 0.4s ease",
                      background:
                        "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(45,45,45,0.95) 100%)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      backdropFilter: "blur(10px)",
                      "&:hover": {
                        transform: "translateY(-12px) scale(1.02)",
                        boxShadow: `0 20px 60px ${stat.color}40`,
                        borderColor: stat.color,
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        mx: "auto",
                        mb: 2,
                        bgcolor: stat.color,
                        width: 64,
                        height: 64,
                        boxShadow: `0 8px 30px ${stat.color}50`,
                      }}
                    >
                      {stat.icon}
                    </Avatar>
                    <Typography
                      variant="h3"
                      fontWeight={700}
                      sx={{ color: "#fff" }}
                    >
                      {loading ? "..." : stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.6)" }}
                    >
                      {stat.label}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grow>
      </Container>

      {/* Species Detection Section */}
      <Box
        sx={{
          py: 8,
          background: "linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)",
          position: "relative",
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            fontWeight={700}
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 2,
              color: "#fff",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Species Detected
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 5,
              color: "rgba(255,255,255,0.7)",
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Tracking and monitoring wildlife species across the region
          </Typography>

          <Grid container spacing={3}>
            {speciesDetected.map((species, index) => (
              <Grid item xs={6} sm={4} md={2} key={index}>
                <Grow in timeout={800 + index * 100}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      borderRadius: 4,
                      transition: "all 0.4s ease",
                      background:
                        "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(45,45,45,0.95) 100%)",
                      border: `1px solid ${species.color}40`,
                      "&:hover": {
                        transform: "translateY(-8px) scale(1.05)",
                        boxShadow: `0 20px 50px ${species.color}40`,
                        borderColor: species.color,
                      },
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -50,
                        right: -50,
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        background: `${species.color}20`,
                      }}
                    />
                    <Avatar
                      sx={{
                        mx: "auto",
                        mb: 2,
                        bgcolor: species.color,
                        width: 56,
                        height: 56,
                        boxShadow: `0 8px 30px ${species.color}50`,
                      }}
                    >
                      {species.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{ color: "#fff" }}
                    >
                      {species.name}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{ color: species.color }}
                    >
                      {species.count}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.5)" }}
                    >
                      Detected
                    </Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Alternate Sections with Images */}
      {sections.map((section, index) => (
        <Box
          key={index}
          sx={{
            py: 8,
            background:
              index % 2 === 0
                ? "linear-gradient(180deg, #0d0d0d 0%, #1a1a1a 100%)"
                : "linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={6} alignItems="center">
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  order: section.imageLeft ? 1 : 2,
                }}
              >
                <Fade in timeout={1000}>
                  <Box>
                    <Chip
                      label={section.stats}
                      sx={{
                        mb: 2,
                        bgcolor: "rgba(255,255,255,0.08)",
                        color: "#4CAF50",
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        border: "1px solid rgba(76,175,80,0.3)",
                      }}
                    />
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      gutterBottom
                      sx={{ color: "#fff" }}
                    >
                      {section.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        mb: 3,
                        lineHeight: 1.8,
                      }}
                    >
                      {section.description}
                    </Typography>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        background:
                          "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        "&:hover": {
                          transform: "translateX(8px)",
                          boxShadow: "0 8px 30px rgba(76,175,80,0.4)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Learn More
                    </Button>
                  </Box>
                </Fade>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  order: section.imageLeft ? 2 : 1,
                }}
              >
                <Grow in timeout={1000}>
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                      aspectRatio: "4/3",
                      background:
                        "linear-gradient(135deg, rgba(26,26,26,0.8) 0%, rgba(45,45,45,0.8) 100%)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={section.image}
                      alt={section.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.6s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        left: 20,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        background: "rgba(0,0,0,0.6)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.8)" }}
                      >
                        {section.title}
                      </Typography>
                    </Box>
                  </Box>
                </Grow>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ))}

      {/* Features Section */}
      <Box
        sx={{
          py: 8,
          background: "linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)",
          position: "relative",
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            fontWeight={700}
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 2,
              color: "#fff",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Platform Features
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 5,
              color: "rgba(255,255,255,0.7)",
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Cutting-edge technology for wildlife conservation and monitoring
          </Typography>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 4,
                    transition: "all 0.4s ease",
                    background:
                      "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(45,45,45,0.95) 100%)",
                    border: "1px solid rgba(255,255,255,0.05)",
                    "&:hover": {
                      transform: "translateY(-12px)",
                      borderColor: feature.color,
                      boxShadow: `0 20px 60px ${feature.color}30`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      color: feature.color,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight={600}
                    textAlign="center"
                    sx={{ color: "#fff" }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    textAlign="center"
                    sx={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Quick Actions Section */}
      <Paper
        sx={{
          p: 6,
          borderRadius: 0,
          ...gradientStyle,
          color: "white",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 -10px 40px rgba(0,0,0,0.3)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "100%",
            height: "100%",
            background:
              "radial-gradient(circle at top right, rgba(76,175,80,0.1) 0%, transparent 50%), radial-gradient(circle at bottom left, rgba(33,150,243,0.1) 0%, transparent 50%)",
            animation: "pulse 4s ease-in-out infinite",
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography variant="h4" sx={{ color: "#d6d6d6" }} fontWeight={700}>
            Ready to Start Monitoring?
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, opacity: 0.9, maxWidth: 600, mx: "auto" }}
          >
            Join the fight for wildlife conservation with our advanced
            monitoring system
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<ExploreIcon />}
              sx={{
                background:
                  "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
                px: 5,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 700,
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 40px rgba(76,175,80,0.4)",
                },
                transition: "all 0.3s ease",
              }}
              href="/dashboard"
            >
              View Dashboard
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                px: 5,
                py: 1.5,
                borderRadius: 3,
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  transform: "translateY(-4px)",
                },
                transition: "all 0.3s ease",
              }}
              href="/cameras"
            >
              Browse Cameras
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Footer */}
      <Box
        sx={{
          py: 4,
          textAlign: "center",
          background: "#0d0d0d",
          borderTop: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
          © {new Date().getFullYear()} Early Warning System. All rights
          reserved.
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "rgba(255,255,255,0.4)", display: "block", mt: 0.5 }}
        >
          Powered by LUMS
        </Typography>
      </Box>
    </Box>
  );
}
